import { ApiQueryParams } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import { MultiObjectSelectInput } from '@frontend/components/basic-forms';
import { EquipmentGroupClient, ProductCategoryClient, ProductGroupClient, QuestionnaireClient, UserGroupClient } from '@frontend/group/api';
import { GroupListResponse, GroupQueryParams, IGroup } from '@frontend/group/types';
import { getCorrectTranslation, useLocale } from '@frontend/lang';
import React, { useState } from 'react';

const ID = 'multi-group-select';
export interface MultiGroupSelectProps {
    id?: string;
    label?: React.ReactNode;
    required?: boolean;
    onChange?: (newValue: string[]) => void;
    onChangeObject?: (newValue: IGroup[]) => void;
    value?: string[];
    searchValueOverwrite?: string;
    className?: string;
    disabled?: boolean;
    isClearable?: boolean;
    allowURLChange?: boolean;
    createEnabled?: boolean;
    queryParams?: ApiQueryParams<GroupQueryParams>;
    disableFetch?: boolean;
    subset?: string[];
    onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    isOpenOverwrite?: boolean;
    queryKeyOverride?: string;
    fetchOverwrite?: (queryParams?: ApiQueryParams<GroupQueryParams>) => Promise<GroupListResponse>;
    itemLabelOverwrite?: (item: IGroup) => string;
    type: EntityType;
}

export const MultiGroupSelect = (props: MultiGroupSelectProps) => {
    const { locale } = useLocale();
    const [v, cV] = useState<IGroup[] | null>(null);
    return (
        <MultiObjectSelectInput<IGroup, GroupQueryParams>
            {...props}
            id={`${ID}${props.id && '-' + props.id}`}
            key={`${ID}${props.id && '-' + props.id}`}
            label={props.label || 'Groups'}
            className={props.className}
            onChange={(v) => {
                props.onChange && props.onChange(v.map((group) => group.id));
                props.onChangeObject && props.onChangeObject(v);
                cV(v);
            }}
            value={props.value || v?.map((group) => group.id) || []}
            multiFetch={(ids) => {
                switch (props.type) {
                    case EntityType.USER_GROUP:
                        return UserGroupClient.resolveUserGroups(ids).then((res) => res.results);
                    case EntityType.EQUIPMENT_GROUP:
                        return EquipmentGroupClient.resolveEquipmentGroups(ids).then((res) => res.results);
                    case EntityType.PRODUCT_GROUP:
                        return ProductGroupClient.resolveProductGroups(ids).then((res) => res.results);
                    case EntityType.PRODUCT_CATEGORY:
                        return ProductCategoryClient.resolveProductCategories(ids).then((res) => res.results);
                    case EntityType.QUESTIONNAIRE:
                        return QuestionnaireClient.resolveQuestionnaires(ids).then((res) => res.results);
                }
                throw new Error(`Unknown group type ${props.type}`);
            }}
            fetch={(arg) => {
                switch (props.type) {
                    case EntityType.USER_GROUP:
                        return UserGroupClient.fetchUserGroups({ ...arg, sort_field: 'name', sort_direction: 'asc' });
                    case EntityType.EQUIPMENT_GROUP:
                        return EquipmentGroupClient.fetchEquipmentGroups({ ...arg, sort_field: 'name', sort_direction: 'asc' });
                    case EntityType.PRODUCT_GROUP:
                        return ProductGroupClient.fetchProductGroups({ ...arg, sort_field: 'name', sort_direction: 'asc' });
                    case EntityType.PRODUCT_CATEGORY:
                        return ProductCategoryClient.fetchProductCategories({ ...arg, sort_field: 'name', sort_direction: 'asc' });
                    case EntityType.QUESTIONNAIRE:
                        return QuestionnaireClient.fetchQuestionnaires({ ...arg, sort_field: 'name', sort_direction: 'asc' });
                }
                throw new Error(`Unknown group type ${props.type}`);
            }}
            itemLabel={(group) => (props.type === EntityType.PRODUCT_CATEGORY ? getCorrectTranslation(locale, group, 'name') : group.name)}
        />
    );
};
